import React, { useRef } from 'react';
import { IconCertificate, IconFileText, IconFlame, IconRosetteDiscountCheck } from '@tabler/icons-react';
import { Anchor, Glow, Pill, Wires, Sparks, gsap, travel, flare, blink, ambience, entrance, useSceneAnimation } from '../../../../../../../../components/Scene';
import './styles.scss';

// Board: the viral pattern writes the hook, the official source backs the fact (its
// citation pops in), both together close with the call to action, and the script
// earns its "with authority" seal. Drawn in the 75–325 band of the stage.
const ROWS = [
  { label: 'artRowHook', tone: 'warm' },
  { label: 'artRowFact', tone: 'cool', cited: true },
  { label: 'artRowCta', tone: 'violet' },
];
const WIRES = [
  { id: 'viral', tone: 'warm', d: 'M96 284 V230', joints: [[96, 284], [96, 230]] },
  { id: 'source', tone: 'cool', d: 'M264 284 V230', joints: [[264, 284], [264, 230]] },
];
const SPARKS = [[130, 262], [230, 256], [180, 246], [74, 120], [290, 110]];
const STEP = 1.7;

function build(q, { still }) {
  if (still) return;
  ambience(q);
  const intro = entrance(q);
  const fills = q('[data-fill]');
  const labels = q('[data-row-label]');
  const cite = q('[data-cite]');
  const seal = q('[data-seal]');
  const reset = () => {
    gsap.set(fills, { scaleX: 0 });
    gsap.set(labels, { opacity: 0.45 });
    gsap.set([cite, seal], { opacity: 0, scale: 0.6 });
  };
  reset();
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.4, delay: intro.duration() - 0.2, onRepeat: reset });
  const write = (i, at) => loop.to(q(`[data-fill="${i}"]`), { scaleX: 1, duration: 0.6, ease: 'power2.out' }, at)
    .to(q(`[data-row-label="${i}"]`), { opacity: 1, duration: 0.3 }, at);
  const feed = (id, at) => {
    flare(loop, q, id, at);
    blink(loop, q, [`${id}-0`], at + 0.1);
    travel(loop, q, id, at + 0.12, 0.6);
    blink(loop, q, [`${id}-1`], at + 0.7);
  };
  feed('viral', 0);
  write(0, 0.75);
  feed('source', STEP);
  write(1, STEP + 0.75);
  loop.to(cite, { opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(2.2)' }, STEP + 1.1);
  feed('viral', STEP * 2);
  feed('source', STEP * 2);
  write(2, STEP * 2 + 0.75);
  loop.to(seal, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(2)' }, STEP * 2 + 1.3)
    .to([fills, labels, cite, seal], { opacity: (n, el) => (el.hasAttribute('data-row-label') ? 0.45 : 0), duration: 0.5 }, '+=1.4')
    .set(fills, { opacity: 1, scaleX: 0 });
}

export default function AuthorityScene({ t }) {
  const stage = useRef(null);
  useSceneAnimation(stage, build);
  return <div className="studio-feature-art__stage" ref={stage}>
    <Glow x={96} y={290} tone="warm" size={160} />
    <Glow x={264} y={290} tone="cool" size={160} />
    <Glow x={180} y={170} tone="violet" size={210} />
    <Sparks points={SPARKS} />
    <Wires wires={WIRES} />
    <Anchor x={180} y={88}>
      <span className="studio-feature-art__badge studio-landing-art__seal is-cool" data-seal><IconRosetteDiscountCheck aria-hidden="true" />{t('artWithAuthority')}</span>
    </Anchor>
    <Anchor x={180} y={171}>
      <div className="studio-feature-art__card studio-landing-art__script" data-enter>
        <div className="studio-landing-art__list-head"><IconFileText aria-hidden="true" /><span>{t('artScript')}</span><b>{t('reels')}</b></div>
        {ROWS.map(({ label, tone, cited }, i) => <div key={label} className="studio-landing-art__row">
          <span className="studio-landing-art__row-name" data-row-label={i}>{t(label)}</span>
          <span className="studio-landing-art__track"><span className={`studio-landing-art__fill is-${tone}`} data-fill={i} /></span>
          {cited && <span className="studio-landing-art__cite is-cool" data-cite><IconCertificate aria-hidden="true" />{t('artSource')}</span>}
        </div>)}
      </div>
    </Anchor>
    <Anchor x={96} y={300}><Pill id="viral" tone="warm" icon={IconFlame}>{t('artViralPattern')}</Pill></Anchor>
    <Anchor x={264} y={300}><Pill id="source" tone="cool" icon={IconCertificate}>{t('artOfficialSource')}</Pill></Anchor>
  </div>;
}
